import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { addPricing, createProduct, getProduct, updatePricing, updateProduct } from "../api/products";

// Day-grid cell -> pricing bucket
const BUCKET_FOR_CELL = { AM: "AM_PM", PM: "AM_PM", FULL_DAY: "FULL_DAY", EVE: "EVENING" };
const BUCKETS = [
  { key: "AM_PM", label: "Morning / afternoon" },
  { key: "FULL_DAY", label: "Full day" },
  { key: "EVENING", label: "Evening" },
];
const TYPE_CODES = ["TRIP", "ACTIVITY"];
// How the quantity is counted. Other rules (staff, tuition…) are kept as they are but not offered.
const COUNTS = [
  { key: "PER_SLOT_PAX", label: "Students × times it's in the grid" },
  { key: "PER_STUDENT", label: "Students, once" },
  { key: "FIXED", label: "A fixed quantity (set on the quote)" },
];
// Legacy rows may have slot null / "AM" / "PM"; they price the half-day cells.
const bucketOf = (slot) => (slot === "FULL_DAY" || slot === "EVENING" ? slot : "AM_PM");

const normalise = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]/g, "");

function editDistance(a, b) {
  const dp = Array.from({ length: a.length + 1 }, (_, i) => [i]);
  for (let j = 1; j <= b.length; j++) dp[0][j] = j;
  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      dp[i][j] = Math.min(
        dp[i - 1][j] + 1,
        dp[i][j - 1] + 1,
        dp[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1),
      );
    }
  }
  return dp[a.length][b.length];
}

/** Catalogue products whose names look like `name`: same letters, a typo or two apart, or one inside the other. */
function similarProducts(name, products, excludeId) {
  const n = normalise(name);
  if (n.length < 4) return [];
  return products.filter((p) => {
    if (p.id === excludeId) return false;
    const m = normalise(p.name);
    if (!m) return false;
    if (m === n) return true;
    if (Math.min(m.length, n.length) >= 6 && (m.includes(n) || n.includes(m))) return true;
    return editDistance(m, n) <= Math.max(2, Math.floor(n.length / 8));
  });
}

/**
 * Create or edit a trip or activity without leaving the quote: the product
 * (quantity = students × times it's in the grid) and its pricing rows for this
 * quote's location and season, one per slot it can go in.
 */
export default function QuickProductDialog({
  open,
  productId,          // set = edit that product; unset = create
  initialName = "",
  cellSlot,
  productTypes,
  existingProducts = [],
  locationId,
  seasonId,
  startDate,
  onClose,
  onSaved,            // (product) after create or edit
  onUseExisting,      // (product) when the user picks a similar existing product instead
}) {
  const editing = !!productId;
  const [form, setForm] = useState({ name: "", productTypeId: "", costPerHead: "", tripCost: "", sellPrice: "", buckets: [] });
  const [loaded, setLoaded] = useState(null); // edit mode: { product, contextRows }
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setError("");
    setLoaded(null);
    if (!editing) {
      const trip = productTypes.find((t) => t.code === "TRIP") || productTypes.find((t) => TYPE_CODES.includes(t.code));
      setForm({
        name: initialName,
        productTypeId: trip ? trip.id : "",
        costPerHead: "",
        tripCost: "",
        sellPrice: "",
        derivation: "PER_SLOT_PAX",
        buckets: [BUCKET_FOR_CELL[cellSlot] || "AM_PM"],
      });
      return;
    }
    setLoading(true);
    getProduct(productId)
      .then((p) => {
        // Rows for exactly this quote's location and season are the ones this dialog edits.
        const contextRows = (p.pricingRows || []).filter(
          (r) => (r.locationId ?? null) === (locationId ?? null) && (r.seasonId ?? null) === (seasonId ?? null),
        );
        const live = contextRows.filter((r) => r.available !== false);
        setLoaded({ product: p, contextRows });
        setForm({
          name: p.name || "",
          productTypeId: p.productTypeId || "",
          costPerHead: live[0]?.unitCost ?? contextRows[0]?.unitCost ?? "",
          tripCost: live[0]?.tripCost ?? contextRows[0]?.tripCost ?? "",
          sellPrice: live[0]?.sellPrice ?? contextRows[0]?.sellPrice ?? "",
          derivation: p.derivation || "PER_SLOT_PAX",
          buckets: [...new Set(live.map((r) => bucketOf(r.slot)))],
        });
      })
      .catch((e) => setError(e.message || "Failed to load product"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, productId]);

  const types = productTypes.filter(
    (t) => TYPE_CODES.includes(t.code) || t.id === loaded?.product?.productTypeId,
  );
  const lookalikes = useMemo(
    () => similarProducts(form.name, existingProducts, productId),
    [form.name, existingProducts, productId],
  );

  const toggleBucket = (key) => setForm((f) => ({
    ...f,
    buckets: f.buckets.includes(key) ? f.buckets.filter((b) => b !== key) : [...f.buckets, key],
  }));

  const submit = async () => {
    if (!form.name.trim()) { setError("Name is required"); return; }
    if (!form.productTypeId) { setError("Type is required"); return; }
    // A coach-only trip has no per-student cost, so either cost will do.
    if (form.costPerHead === "" && form.tripCost === "") {
      setError("Enter a cost per student, a coach cost, or both"); return;
    }
    if (Number(form.costPerHead) < 0) { setError("Cost per student can't be negative"); return; }
    if (form.buckets.length === 0) { setError("Pick at least one slot"); return; }
    // Pricing resolves against the quote's start date (or today), so new rows
    // must already be in effect by then.
    const today = new Date().toISOString().slice(0, 10);
    const effectiveFrom = startDate && startDate < today ? startDate : today;
    const unitCost = String(Number(form.costPerHead) || 0);
    if (form.tripCost !== "" && Number(form.tripCost) < 0) { setError("Coach cost can't be negative"); return; }
    const tripCost = form.tripCost === "" ? null : String(Number(form.tripCost));
    if (form.sellPrice !== "" && Number(form.sellPrice) < 0) { setError("Sell price can't be negative"); return; }
    const sellPrice = form.sellPrice === "" ? null : String(Number(form.sellPrice));
    try {
      setSaving(true);
      setError("");
      let product;
      if (editing) {
        const p = loaded.product;
        product = await updateProduct(p.id, {
          name: form.name.trim(),
          description: p.description,
          productTypeId: Number(form.productTypeId),
          costBasis: p.costBasis,
          derivation: form.derivation,
          // The three rules offered here take no params; keep any others' params as they were.
          derivationParams: form.derivation === p.derivation ? p.derivationParams : null,
          defaultMarkupPct: p.defaultMarkupPct,
          markupExempt: p.markupExempt,
          isActive: p.isActive,
        });
        for (const bucket of BUCKETS.map((b) => b.key)) {
          const wanted = form.buckets.includes(bucket);
          const rows = loaded.contextRows.filter((r) => bucketOf(r.slot) === bucket);
          for (const r of rows) {
            await updatePricing(p.id, r.id, {
              ...r,
              unitCost,
              tripCost,
              sellPrice,
              available: wanted,
            });
          }
          if (wanted && rows.length === 0) {
            await addPricing(p.id, { locationId, seasonId, slot: bucket, unitCost, tripCost, sellPrice, effectiveFrom, available: true });
          }
        }
      } else {
        product = await createProduct({
          name: form.name.trim(),
          productTypeId: Number(form.productTypeId),
          costBasis: "per_session",
          derivation: form.derivation,
          derivationParams: null,
          markupExempt: false,
          isActive: true,
        });
        for (const slot of form.buckets) {
          await addPricing(product.id, {
            locationId: locationId || null,
            seasonId: seasonId || null,
            slot,
            unitCost,
            tripCost,
            sellPrice,
            effectiveFrom,
            available: true,
          });
        }
      }
      onSaved?.(product);
    } catch (e) {
      setError(e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{editing ? "Edit trip or activity" : "New trip or activity"}</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}><CircularProgress size={28} /></Box>
        ) : (
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField
              label="Name" required fullWidth autoFocus
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
            {!editing && lookalikes.length > 0 && (
              <Alert severity="warning" sx={{ alignItems: "flex-start" }}>
                <Typography variant="body2" sx={{ mb: 0.5 }}>Already in the catalogue:</Typography>
                {lookalikes.slice(0, 3).map((p) => (
                  <Stack key={p.id} direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                    <Typography variant="body2" fontWeight={600}>{p.name}</Typography>
                    <Button size="small" onClick={() => onUseExisting?.(p)}>Use this</Button>
                  </Stack>
                ))}
              </Alert>
            )}
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                label="Type"
                value={form.productTypeId}
                onChange={(e) => setForm({ ...form, productTypeId: e.target.value })}
              >
                {types.map((t) => <MenuItem key={t.id} value={t.id}>{t.name || t.code}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Quantity</InputLabel>
              <Select
                label="Quantity"
                value={form.derivation}
                onChange={(e) => setForm({ ...form, derivation: e.target.value })}
              >
                {COUNTS.map((c) => <MenuItem key={c.key} value={c.key}>{c.label}</MenuItem>)}
                {!COUNTS.some((c) => c.key === form.derivation) && (
                  <MenuItem value={form.derivation}>{form.derivation} (set in Admin)</MenuItem>
                )}
              </Select>
            </FormControl>
            <TextField
              label="Cost per student" fullWidth type="number"
              inputProps={{ min: 0, step: "0.01" }}
              InputProps={{ startAdornment: <InputAdornment position="start">£</InputAdornment> }}
              value={form.costPerHead}
              onChange={(e) => setForm({ ...form, costPerHead: e.target.value })}
              helperText="e.g. the entry ticket. Leave blank if there isn't one."
            />
            <TextField
              label="Coach cost per trip (optional)" fullWidth type="number"
              inputProps={{ min: 0, step: "0.01" }}
              InputProps={{ startAdornment: <InputAdornment position="start">£</InputAdornment> }}
              value={form.tripCost}
              onChange={(e) => setForm({ ...form, tripCost: e.target.value })}
              helperText="Added once each time it's in the grid."
            />
            <TextField
              label="Sell price per student (optional)" fullWidth type="number"
              inputProps={{ min: 0, step: "0.01" }}
              InputProps={{ startAdornment: <InputAdornment position="start">£</InputAdornment> }}
              value={form.sellPrice}
              onChange={(e) => setForm({ ...form, sellPrice: e.target.value })}
              helperText="Leave blank to charge cost + the quote's markup. Set, it's charged at this price instead; £0 includes it free."
            />
            <Box>
              <Typography variant="body2" color="text.secondary">Can go in</Typography>
              <FormGroup row>
                {BUCKETS.map((b) => (
                  <FormControlLabel
                    key={b.key}
                    control={<Checkbox size="small" checked={form.buckets.includes(b.key)} onChange={() => toggleBucket(b.key)} />}
                    label={<Typography variant="body2">{b.label}</Typography>}
                  />
                ))}
              </FormGroup>
            </Box>
            <Typography variant="caption" color="text.secondary">
              {editing
                ? "Changes apply to this location and season. Draft quotes pick them up when they're next recalculated."
                : "Priced for this quote's location and season, and saved to the catalogue for later quotes."}
            </Typography>
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={saving || loading}>
          {saving ? "Saving…" : editing ? "Save" : "Create and add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
